// src/App.js
// BeautySalon v2.0.1 — Routing หลักของระบบ POS

import React, { useContext } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";

import { AuthProvider } from "./AuthContext";
import AuthContext from "./AuthContext";
import { NotificationProvider } from "./NotificationContext";
import { ReminderProvider } from "./ReminderContext";
import ReminderModal from "./components/ReminderModal";

import Navbar from "./components/Navbar";
import ToastNotification from "./components/ToastNotification";

// === Pages ===
import LoginPage from "./pages/LoginPage";
import TransactionsPage from "./pages/TransactionsPage"; // POS / Orders
import ServicesPage from "./pages/ServicesPage";
import EmployeesPage from "./pages/EmployeesPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import DashboardPage from "./pages/DashboardPage";
import CommissionsPage from "./pages/CommissionsPage";
import CashflowPage from "./pages/CashflowPage";
import DeductionsPage from "./pages/DeductionsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PrintBillPage from "./pages/PrintBillPage";
import BarcodePage from "./pages/BarcodePage";

// ... (imports)

// ...
import AppointmentsPage from "./pages/AppointmentsPage";
import ExpensesPage from "./pages/ExpensesPage";
import ProfitPage from "./pages/ProfitPage";
import DiscountCodesPage from "./pages/DiscountCodesPage";

import CustomersPage from "./pages/CustomersPage";
import DepositsPage from "./pages/DepositsPage";

// ===================================================
// Route protection components
// ===================================================

// ✅ ProtectedRoute: เช็ก Login + Permission
const ProtectedRoute = ({ children, requiredPermission }) => {
    const { user } = useContext(AuthContext);

    // 1. เช็กว่า Login หรือยัง
    if (!user) return <Navigate to="/login" replace />;

    // 2. ถ้ามี requiredPermission ให้เช็กสิทธิ์
    if (requiredPermission) {
        // Admin เข้าได้เสมอ
        if (user.role === 'admin') return children;

        // เช็กว่ามีสิทธิ์นี้ในรายการ permissions หรือไม่
        if (!user.permissions?.includes(requiredPermission)) {
            // ถ้าไม่มีสิทธิ์ ให้เด้งไปหน้าแรกที่เข้าได้ (เช่น POS) หรือหน้า Login
            return <Navigate to="/pos" replace />;
        }
    }

    return children;
};

// ✅ สำหรับหน้า admin เท่านั้น (เช่น Settings, Employees)
const AdminRoute = ({ children }) => {
    const { user } = useContext(AuthContext);
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/pos" replace />;
    return children;
};

// ===================================================
// Main Application
// ===================================================

// Basic Error Boundary
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ error, errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="container mt-5">
                    <div className="alert alert-danger">
                        <h4><i className="bi bi-exclamation-triangle-fill me-2"></i>Something went wrong.</h4>
                        <details style={{ whiteSpace: 'pre-wrap' }}>
                            {this.state.error && this.state.error.toString()}
                            <br />
                            {this.state.errorInfo && this.state.errorInfo.componentStack}
                        </details>
                    </div>
                    <button className="btn btn-secondary" onClick={() => window.location.href = '/'}>Go to Home</button>
                </div>
            );
        }

        return this.props.children;
    }
}

const App = () => {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <NotificationProvider>
                    <ReminderProvider>
                        <Router>
                            <ToastNotification />
                            <div className="app-layout">
                                <Navbar />
                                <div className="app-content">
                                    <ReminderModal />
                                    <div className="container my-3">
                                        <Routes>
                                            {/* =============== Public =============== */}
                                            <Route path="/login" element={<LoginPage />} />

                                            {/* =============== Protected (Core Permissions) =============== */}
                                            <Route
                                                path="/pos"
                                                element={
                                                    <ProtectedRoute requiredPermission="pos">
                                                        <TransactionsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/barcode"
                                                element={
                                                    <ProtectedRoute requiredPermission="pos">
                                                        <BarcodePage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/services"
                                                element={
                                                    <ProtectedRoute requiredPermission="services">
                                                        <ServicesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/orders/:orderId/print"
                                                element={
                                                    <ProtectedRoute requiredPermission="pos">
                                                        <PrintBillPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/appointments"
                                                element={
                                                    <ProtectedRoute requiredPermission="appointments">
                                                        <AppointmentsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/customers"
                                                element={
                                                    <ProtectedRoute requiredPermission="customers">
                                                        <CustomersPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/deposits"
                                                element={
                                                    <ProtectedRoute requiredPermission="customers">
                                                        <DepositsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/dashboard"
                                                element={
                                                    <ProtectedRoute requiredPermission="dashboard">
                                                        <DashboardPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/cashflow"
                                                element={
                                                    <ProtectedRoute requiredPermission="cashflow">
                                                        <CashflowPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/deductions"
                                                element={
                                                    <ProtectedRoute requiredPermission="deductions">
                                                        <DeductionsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/commissions"
                                                element={
                                                    <ProtectedRoute requiredPermission="commissions">
                                                        <CommissionsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/reports"
                                                element={
                                                    <ProtectedRoute requiredPermission="reports">
                                                        <ReportsPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/expenses"
                                                element={
                                                    <ProtectedRoute requiredPermission="expenses">
                                                        <ExpensesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/profit"
                                                element={
                                                    <ProtectedRoute requiredPermission="profit">
                                                        <ProfitPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route
                                                path="/discount-codes"
                                                element={
                                                    <ProtectedRoute requiredPermission="discount_codes">
                                                        <DiscountCodesPage />
                                                    </ProtectedRoute>
                                                }
                                            />

                                            {/* =============== Admin Only =============== */}
                                            <Route
                                                path="/employees"
                                                element={
                                                    <AdminRoute>
                                                        <EmployeesPage />
                                                    </AdminRoute>
                                                }
                                            />

                                            <Route
                                                path="/settings"
                                                element={
                                                    <AdminRoute>
                                                        <SettingsPage />
                                                    </AdminRoute>
                                                }
                                            />

                                            {/* =============== Default redirect =============== */}
                                            <Route path="*" element={<Navigate to="/pos" replace />} />
                                        </Routes>
                                    </div>
                                </div>
                            </div>
                        </Router>
                    </ReminderProvider>
                </NotificationProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
};

export default App;


